import { useDepartmentText } from 'model/DepartmentTerminology';
import { useApi } from 'api/api-context';
import { DataSelectInheritedProps, DataSelect } from 'components/selects/DataSelect';
import { nanoid } from 'nanoid';
import { useCallback, useMemo, useState } from 'react';

interface DeviceResponsiblePersonSelectProp {
  departmentId: number;
}

interface ResponsiblePersonOption {
  label: string;
  value: number;
  isContactPerson: boolean;
}

/**
 * Fetches users from API.
 */
function useDeviceResponsiblePersonSelect({ departmentId }: DeviceResponsiblePersonSelectProp) {
  const { getAction } = useApi();

  const fetchUsers = useCallback(async () => {
    const action = getAction('DeviceResponsiblePersonGetOptions');

    const users = await action({ parameters: { departmentId: String(departmentId) } });

    return users.map(({ fullName, userId, isContactPerson }) => ({
      label: fullName,
      value: userId,
      isContactPerson,
    }));
  }, [getAction, departmentId]);

  return { fetchUsers };
}

type DeviceResponsiblePersonSelectProps = DataSelectInheritedProps & DeviceResponsiblePersonSelectProp;

/**
 * Keeps fetched contact metadata and search state within the selected department.
 */
function ResponsiblePersonSelectInput({
  departmentId,
  filterData,
  onReady,
  searchValue,
  onSearchChange,
  value,
  ...props
}: DeviceResponsiblePersonSelectProps) {
  const departmentText = useDepartmentText();
  const [users, setUsers] = useState<ResponsiblePersonOption[]>([]);
  const [inputSearch, setInputSearch] = useState('');
  const { fetchUsers } = useDeviceResponsiblePersonSelect({ departmentId });
  const formatUserLabels = useCallback(
    (users: ResponsiblePersonOption[]) =>
      users.map((user) => ({
        ...user,
        label:
          user.label +
          (user.isContactPerson
            ? departmentText(' — Kontaktná osoba v položke úrovne', ' — Kontaktná osoba na stredisku')
            : ''),
      })),
    [departmentText]
  );
  const filterUsers = useCallback(
    (users: ResponsiblePersonOption[]) => {
      const options = formatUserLabels(users);
      return filterData ? filterData(options) : options;
    },
    [formatUserLabels, filterData]
  );
  const handleReady = useCallback(
    (options: ResponsiblePersonOption[]) => {
      setUsers(options);
      onReady?.(formatUserLabels(options));
    },
    [onReady, formatUserLabels]
  );
  const handleSearchChange = useCallback(
    (search: string) => {
      setInputSearch(search);
      onSearchChange?.(search);
    },
    [onSearchChange]
  );
  const selectedUser = users.find((user) => String(user.value) === String(value));
  const currentSearch = searchValue ?? inputSearch;
  const objectLabel = selectedUser?.label + ' — Kontaktná osoba v položke úrovne';
  const strediskoLabel = selectedUser?.label + ' — Kontaktná osoba na stredisku';
  // Mantine keeps the search input while its dropdown is open. Relabel only the
  // selected contact's complete generated label, preserving manually typed queries.
  const formattedSearch =
    selectedUser?.isContactPerson && (currentSearch === objectLabel || currentSearch === strediskoLabel)
      ? departmentText(objectLabel, strediskoLabel)
      : currentSearch;

  return (
    <DataSelect
      action={fetchUsers}
      filterData={filterUsers}
      onReady={handleReady}
      searchValue={formattedSearch}
      onSearchChange={handleSearchChange}
      value={value}
      labelProp="label"
      valueProp="value"
      {...props}
    />
  );
}

/**
 * Select input for users; changing departments resets its cached options and search.
 */
export function DeviceResponsiblePersonSelect({ departmentId, ...props }: DeviceResponsiblePersonSelectProps) {
  const key = useMemo(() => nanoid(), [departmentId]);
  return <ResponsiblePersonSelectInput key={key} departmentId={departmentId} {...props} />;
}
